import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Empty, List, Popover, Typography } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";

import StatusTag from "@/components/StatusTag";
import {
  isInFlight,
  useOperationsStore,
  type TrackedOperation,
} from "@/stores/operationsStore";

const KIND_LABEL: Record<TrackedOperation["kind"], string> = {
  instance: "Instance",
  volume: "Volume",
  loadbalancer: "Load balancer",
  snapshot: "Snapshot",
  cluster: "Kubernetes cluster",
  function: "Function",
  database: "Database",
  other: "Operation",
};

/** Where a finished operation's resource lives — rows deep-link there once
 *  the workflow surfaces a resultId. */
const KIND_ROUTE: Record<
  TrackedOperation["kind"],
  ((id: string) => string) | null
> = {
  instance: (id) => `/compute/${id}`,
  volume: (id) => `/storage/${id}`,
  loadbalancer: (id) => `/lbaas/${id}`,
  // A server snapshot's result is a Glance image.
  snapshot: (id) => `/images/${id}`,
  // A function build's result id is the function name.
  function: (id) => `/functions/${id}`,
  cluster: (id) => `/k8s/${id}`,
  database: (id) => `/database/${id}`,
  other: null,
};

/** Compact "started N ago" so the panel reads as a history, not just a live list. */
function timeAgo(startedAt: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * Top-bar activity feed: a badge with the in-flight count opening a list of
 * tracked operations and their live status. Gives the portal the "system
 * status visibility" cloud consoles have. Driven by the operations store; the
 * statuses are kept fresh by <OperationsTracker>. Rows whose operation has
 * surfaced a result id navigate to that resource's detail page.
 */
export default function ActivityPanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const operations = useOperationsStore((s) => s.operations);
  const clearFinished = useOperationsStore((s) => s.clearFinished);
  const inFlightCount = operations.filter(isInFlight).length;
  const hasFinished = operations.some((o) => !isInFlight(o));

  const content = (
    <div style={{ width: 320, maxHeight: 360, overflowY: "auto" }}>
      {operations.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No recent activity"
        />
      ) : (
        <List<TrackedOperation>
          size="small"
          dataSource={operations}
          renderItem={(op) => {
            const toRoute = KIND_ROUTE[op.kind];
            const route =
              op.resultId && toRoute ? toRoute(op.resultId) : undefined;
            return (
              <List.Item
                {...(route
                  ? {
                      style: { cursor: "pointer" },
                      onClick: () => {
                        setOpen(false);
                        navigate(route);
                      },
                    }
                  : {})}
              >
                <List.Item.Meta
                  title={op.label}
                  description={
                    <>
                      {KIND_LABEL[op.kind]} · {timeAgo(op.startedAt)}
                      {op.error ? (
                        <>
                          <br />
                          <Typography.Text
                            type="danger"
                            style={{ fontSize: 12 }}
                          >
                            {op.error}
                          </Typography.Text>
                        </>
                      ) : null}
                    </>
                  }
                />
                <StatusTag status={op.status} kind="operation" />
              </List.Item>
            );
          }}
        />
      )}
      {hasFinished && (
        <div style={{ textAlign: "right", paddingTop: 8 }}>
          <Button type="link" size="small" onClick={clearFinished}>
            Clear finished
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      title="Activity"
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
    >
      <Badge count={inFlightCount} size="small" offset={[-2, 2]}>
        <Button type="text" aria-label="Activity" icon={<ThunderboltOutlined />} />
      </Badge>
    </Popover>
  );
}
